/**
 * Générateur — Construire un triangle dont les longueurs des côtés et/ou
 * les mesures des angles sont données (connaître et utiliser sa définition)
 * Attendu ID : 666 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - faisabilite_triangle_qcm         : QCM Oui/Non (inégalité triangulaire, formulation pratique)
 *   - longueur_extreme_saisie          : saisie numérique (borne min ou max du 3e côté)
 *   - methode_construction_qcm         : QCM 4 choix (règle/compas/rapporteur selon données)
 *   - etapes_construction_assoc        : ordonner 5 étapes d'une procédure de construction
 *   - codage_main_levee_qcm            : QCM 4 choix (description textuelle du codage)
 *   - coherence_donnees_vf             : tableau V/F 3V + 3F sur la suffisance des données
 *
 * Spécificité : 100 % texte pur (pas de SVG, pas de rapporteur interactif) pour
 * maximiser la stabilité du fingerprint audit. Le tracé effectif relève d'un
 * autre format (à venir) ; ici on cible les compétences cognitives en amont
 * (décision, planification, codage, vérification).
 *
 * Variantes estimées : plusieurs milliers par format (objectif : 20/20 uniques
 * sur 20 itérations auditGen, stable sur 5 runs).
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeOrdonner,
    makeProgrammeConstruction,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 666;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split(''); // 23 lettres (exclut I, O)

const L_FAIS = 'Faisabilité d\'un triangle (QCM)';
const L_LONG = 'Longueur extrême du 3e côté (saisie)';
const L_METH = 'Choisir la méthode de construction (QCM)';
const L_ETAP = 'Ordre des étapes de construction (ordonner)';
const L_COD  = 'Codage du dessin à main levée (QCM)';
const L_COH  = 'Cohérence des données pour construction unique (V/F)';
const L_CONSTR_SSS = 'Construire un triangle : 3 longueurs (règle + compas)';
const L_CONSTR_SAS = 'Construire un triangle : 2 longueurs + angle entre elles (règle + rapporteur + compas)';
const L_CONSTR_ASA = 'Construire un triangle : 1 longueur + 2 angles adjacents (règle + rapporteur)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_FAIS,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// ─── Pools de données ────────────────────────────────────────

// Triplets constructibles (c < a + b strict, convention a ≤ b ≤ c).
const TRIPLETS_OUI = [
    [3, 4, 5], [3, 4, 6], [4, 5, 6], [5, 5, 5], [4, 5, 7],
    [3, 5, 5], [2, 3, 4], [3, 5, 6], [4, 4, 6], [5, 6, 7],
    [6, 6, 6], [4, 6, 8], [3, 6, 7], [5, 7, 8], [4, 7, 9],
    [6, 7, 8], [5, 8, 10], [4, 8, 9], [3, 7, 8], [6, 8, 9],
    [4, 4, 4], [3, 3, 5], [5, 6, 9], [7, 7, 7], [4, 5, 8],
];

// Triplets non constructibles (c ≥ a + b). Moitié c > a+b, moitié c = a+b (aplati).
const TRIPLETS_NON = [
    [2, 3, 7], [3, 4, 10], [2, 5, 8], [1, 2, 4], [3, 3, 8],
    [4, 5, 11], [2, 4, 9], [3, 5, 10], [4, 4, 9], [2, 6, 10],
    [3, 7, 12], [2, 2, 5], [5, 5, 12], [3, 4, 9], [4, 6, 11],
    // Cas aplati (c = a + b) → non constructible en 6e.
    [3, 4, 7], [2, 5, 7], [4, 5, 9], [3, 3, 6], [5, 6, 11],
    [2, 3, 5], [4, 4, 8], [3, 5, 8], [5, 5, 10], [2, 4, 6],
];

// Paires (a, b) pour Ex. 2 : a < b, a + b ≤ 18. 36 paires.
const PAIRES_EX2 = [
    [2, 3], [2, 4], [2, 5], [2, 6], [2, 7], [2, 8], [2, 9], [2, 10],
    [3, 4], [3, 5], [3, 6], [3, 7], [3, 8], [3, 9], [3, 10],
    [4, 5], [4, 6], [4, 7], [4, 8], [4, 9], [4, 10],
    [5, 6], [5, 7], [5, 8], [5, 9], [5, 10],
    [6, 7], [6, 8], [6, 9], [6, 10],
    [7, 8], [7, 9], [7, 10],
    [8, 9], [8, 10],
    [9, 10],
];

// Longueurs et angles pour Ex. 3, 4, 5, 6.
const LONGUEURS_EX3 = [3, 4, 5, 6, 7, 8, 9, 10];
const ANGLES_EX3 = [30, 40, 45, 50, 60, 70, 75, 80, 100, 110, 120]; // pas 90°

// Les 4 choix fixes de méthodes de construction.
// Deux options seulement peuvent être justes : les instruments ne dépendent que
// de la présence ou non d'un angle dans les données. Les deux autres sont des
// distracteurs, chacun adossé à une erreur repérable — oublier que la règle est
// indispensable pour tracer les côtés, ou croire les données insuffisantes.
// « règle, rapporteur et compas » a été retiré : il n'attrapait que le réflexe
// de tout cocher par prudence.
const CHOIX_METHODES = [
    'règle et compas seulement',
    'compas seulement',
    'règle et rapporteur seulement',
    'on ne peut pas construire ce triangle avec ces seules données',
];

// ─── Ex. 1 — Faisabilité d'un triangle à 3 côtés (QCM) ──────
// 3 choix : Oui / Non / distracteur pédagogique (toujours faux).
// auditGen exige ≥ 3 choix, donc on fournit un 3e choix qui modélise une
// erreur courante : croire qu'on ne peut trancher sans essayer de tracer.
const DISTRACTEUR_FAISABILITE = 'On ne peut pas savoir sans essayer de tracer';

const genFaisabilite = () => {
    const cible = randomChoice(['Oui', 'Non']);
    const pool = cible === 'Oui' ? TRIPLETS_OUI : TRIPLETS_NON;
    const triplet = randomChoice(pool);
    // Permutation aléatoire des 3 longueurs pour varier l'ordre d'apparition.
    const [a, b, c] = shuffle(triplet);

    // 5 variantes d'énoncé : 2 sans lettres + 3 avec lettres (majoritaires pour
    // maximiser la variété de fingerprints).
    const rVariante = Math.random();
    let texte;
    if (rVariante < 0.15) {
        // E1 — sans lettres, formulation "Peut-on construire"
        texte = `<p>Peut-on construire un triangle dont les côtés mesurent ${a} cm, ${b} cm et ${c} cm ?</p>`;
    } else if (rVariante < 0.30) {
        // E2 — sans lettres, formulation "Est-il possible de tracer"
        texte = `<p>Est-il possible de tracer un triangle de côtés ${a} cm, ${b} cm et ${c} cm ?</p>`;
    } else if (rVariante < 0.55) {
        // E3 — avec lettres, formulation "existe-t-il"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>Un triangle ${L1}${L2}${L3} tel que ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm existe-t-il ?</p>`;
    } else if (rVariante < 0.80) {
        // E4 — avec lettres, formulation "Peut-on tracer"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>Peut-on tracer un triangle ${L1}${L2}${L3} avec ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm ?</p>`;
    } else {
        // E5 — avec lettres, formulation "constructible"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>Le triangle ${L1}${L2}${L3} de côtés ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm est-il constructible ?</p>`;
    }

    const payload = makeQcm(['Oui', 'Non', DISTRACTEUR_FAISABILITE], cible, texte, opts(L_FAIS));
    payload._meta.generatorKey = 'faisabilite_triangle_qcm';
    return payload;
};

// ─── Ex. 2 — Longueur extrême du 3e côté (saisie) ───────────
const genLongueurExtreme = () => {
    const sens = randomChoice(['min', 'max']);
    const [a, b] = randomChoice(PAIRES_EX2); // a < b (pool trié)

    // Borne min stricte : 3e côté > b - a. Plus petit entier : (b - a) + 1.
    // Borne max stricte : 3e côté < a + b. Plus grand entier : a + b - 1.
    const bonne = sens === 'min' ? (b - a + 1) : (a + b - 1);

    // Permute l'affichage (a, b) → (u, v) pour varier l'ordre.
    const [u, v] = shuffle([a, b]);

    // Privilégier les variantes lettres pour une variété combinatoire maximale
    // (23³ ≈ 12 k facteurs). 2 formulations sans lettres + 3 avec lettres.
    const rVariante = Math.random();
    let texte;
    const superlatif = sens === 'min' ? 'petite' : 'grande';

    // Les variantes sans lettres sont rares (5 % chacune) car leur combinatoire est
    // limitée (36 × 2 × 2 ≈ 288 fingerprints) et provoque des collisions sur 20 tirages.
    // Les 3 variantes avec lettres dominent (30 % chacune) : 23³ ≈ 12 k multiplicateur.
    if (rVariante < 0.05) {
        // V1 — sans lettres, formulation "Un triangle a deux côtés"
        texte = `<p>Un triangle a deux côtés de ${u} cm et ${v} cm. Quelle est la plus ${superlatif} longueur entière, en cm, que peut avoir le troisième côté pour que le triangle soit constructible ?</p>`;
    } else if (rVariante < 0.10) {
        // V2 — sans lettres, formulation "Deux des côtés d'un triangle mesurent"
        texte = `<p>Deux des côtés d'un triangle mesurent ${u} cm et ${v} cm. Quelle est la plus ${superlatif} longueur entière (en cm) que peut prendre le troisième côté ?</p>`;
    } else if (rVariante < 0.40) {
        // V3 — avec lettres, formulation "est tel que ... existe"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>Le triangle ${L1}${L2}${L3} est tel que ${L1}${L2} = ${u} cm et ${L2}${L3} = ${v} cm. Quelle est la plus ${superlatif} longueur entière possible (en cm) de ${L1}${L3} pour que le triangle existe ?</p>`;
    } else if (rVariante < 0.70) {
        // V4 — avec lettres, formulation "On veut construire"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>On veut construire un triangle ${L1}${L2}${L3} avec ${L1}${L2} = ${u} cm et ${L2}${L3} = ${v} cm. Quelle est la plus ${superlatif} longueur entière (en cm) que peut avoir ${L1}${L3} ?</p>`;
    } else {
        // V5 — avec lettres, formulation "Dans le triangle"
        const [L1, L2, L3] = pickLetters(3);
        texte = `<p>Dans le triangle ${L1}${L2}${L3}, on a ${L1}${L2} = ${u} cm et ${L2}${L3} = ${v} cm. Donne la plus ${superlatif} valeur entière (en cm) que peut prendre ${L1}${L3} pour que le triangle soit constructible.</p>`;
    }

    const payload = makeSaisie(String(bonne), texte, { ...opts(L_LONG), strict: true, suffixe: ' cm' });
    payload._meta.generatorKey = 'longueur_extreme_saisie';
    return payload;
};

// ─── Ex. 3 — Choisir la méthode de construction (QCM) ───────
const genMethode = () => {
    // Le cas A pèse la moitié des tirages : les cas B et C partagent la même
    // réponse (les instruments ne dépendent que de la présence d'un angle), donc
    // un tirage uniforme sur trois cas ferait gagner « règle et rapporteur »
    // deux fois sur trois.
    const cas = randomChoice(['A', 'A', 'B', 'C']);
    const [L1, L2, L3] = pickLetters(3);

    let donnees, bonne;
    if (cas === 'A') {
        const triplet = randomChoice(TRIPLETS_OUI);
        const [a, b, c] = shuffle(triplet);
        donnees = `${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm`;
        bonne = CHOIX_METHODES[0]; // règle et compas
    } else if (cas === 'B') {
        const [a, c] = randomChoiceMultiple(LONGUEURS_EX3, 2);
        let alpha;
        do { alpha = randomChoice(ANGLES_EX3); } while (alpha >= 150); // marge de sécurité
        donnees = `${L1}${L2} = ${a} cm, ${L1}${L3} = ${c} cm et l'angle $\\widehat{${L2}${L1}${L3}}$ = ${alpha}°`;
        // Deux côtés et l'angle compris : on trace le premier côté à la règle
        // graduée, on porte l'angle au rapporteur, on marque le second côté à la
        // règle sur la demi-droite obtenue. Le compas n'intervient nulle part —
        // il n'est nécessaire que lorsque AUCUN angle n'est donné, pour croiser
        // deux arcs. L'exiger comptait faux la méthode correcte.
        bonne = CHOIX_METHODES[2]; // règle et rapporteur seulement
    } else {
        // Cas C : 1 côté + 2 angles. Filtrer alpha + beta < 170 pour garantir un 3e angle >= 10°.
        const b = randomChoice(LONGUEURS_EX3);
        let alpha, beta;
        let tries = 0;
        do {
            alpha = randomChoice(ANGLES_EX3);
            beta = randomChoice(ANGLES_EX3);
            tries++;
        } while ((alpha + beta >= 170 || alpha === beta) && tries < 30);
        // Fallback sûr si trop de retries
        if (alpha + beta >= 170 || alpha === beta) { alpha = 40; beta = 60; }
        donnees = `${L2}${L3} = ${b} cm, l'angle $\\widehat{${L1}${L2}${L3}}$ = ${alpha}° et l'angle $\\widehat{${L1}${L3}${L2}}$ = ${beta}°`;
        bonne = CHOIX_METHODES[2]; // règle et rapporteur
    }

    const variante = randomChoice(['M1', 'M2']);
    const texte = variante === 'M1'
        ? `<p>Pour construire le triangle ${L1}${L2}${L3} avec ${donnees}, quelle méthode utilises-tu ?</p>`
        : `<p>Avec ${donnees}, quels instruments utilises-tu pour construire le triangle ${L1}${L2}${L3} ?</p>`;

    const payload = makeQcm(CHOIX_METHODES, bonne, texte, opts(L_METH));
    payload._meta.generatorKey = 'methode_construction_qcm';
    return payload;
};

// ─── Ex. 4 — Ordre des étapes de construction (ordonner) ────
const etapes3Cotes = (L1, L2, L3, a, b, c) => [
    `Tracer le segment [${L1}${L2}] de longueur ${a} cm à la règle.`,
    `Régler l'écartement du compas à la longueur ${b} cm.`,
    `Tracer un arc de cercle de centre ${L2} et de rayon ${b} cm.`,
    `Régler l'écartement du compas à la longueur ${c} cm.`,
    `Tracer un arc de cercle de centre ${L1} et de rayon ${c} cm. Le point ${L3} est à l'intersection des deux arcs.`,
];

// « première » / « la seconde » ne sont pas des ornements : les deux
// demi-droites sont géométriquement interchangeables, et le scoring de
// `ordonner` compare le tableau DANS L'ORDRE. Sans ces deux mots, l'ordre
// inverse était tout aussi juste et était compté faux.
const etapes1Cote2Angles = (L1, L2, L3, a, alpha, beta) => [
    `Tracer le segment [${L2}${L3}] de longueur ${a} cm à la règle.`,
    `À partir de ${L2}, tracer une première demi-droite faisant un angle de ${alpha}° avec [${L2}${L3}) au rapporteur.`,
    `À partir de ${L3}, tracer la seconde demi-droite, faisant un angle de ${beta}° avec [${L3}${L2}), au rapporteur.`,
    `Repérer le point d'intersection des deux demi-droites — c'est le sommet ${L1} du triangle.`,
    `Gommer ce qui dépasse du triangle ${L1}${L2}${L3}.`,
];

const genEtapes = () => {
    const [L1, L2, L3] = pickLetters(3);
    const methode = Math.random() < 0.60 ? '3cotes' : 'angles';
    const variante = randomChoice(['O1', 'O2']);

    let items, texte;
    if (methode === '3cotes') {
        // Triplets à longueurs TOUTES distinctes uniquement : avec b = c, les
        // deux étapes « Régler l'écartement du compas… » seraient identiques
        // → ordre indécidable au scoring (ordre strict)
        const triplet = randomChoice(TRIPLETS_OUI.filter(t => new Set(t).size === 3));
        const [a, b, c] = shuffle(triplet);
        items = etapes3Cotes(L1, L2, L3, a, b, c);
        texte = variante === 'O1'
            ? `<p>Voici les étapes de la construction du triangle ${L1}${L2}${L3} avec ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm. Remets-les dans le bon ordre.</p>`
            : `<p>Pour construire le triangle ${L1}${L2}${L3} avec trois côtés de longueurs ${a} cm, ${b} cm et ${c} cm, on suit 5 étapes. Remets-les dans l'ordre.</p>`;
    } else {
        const a = randomChoice(LONGUEURS_EX3);
        let alpha, beta;
        let tries = 0;
        do {
            alpha = randomChoice(ANGLES_EX3);
            beta = randomChoice(ANGLES_EX3);
            tries++;
        } while ((alpha + beta >= 170 || alpha === beta) && tries < 30);
        if (alpha + beta >= 170 || alpha === beta) { alpha = 40; beta = 60; }
        items = etapes1Cote2Angles(L1, L2, L3, a, alpha, beta);
        texte = variante === 'O1'
            ? `<p>Voici les étapes de la construction du triangle ${L1}${L2}${L3} avec ${L2}${L3} = ${a} cm, l'angle en ${L2} mesurant ${alpha}° et l'angle en ${L3} mesurant ${beta}°. Remets-les dans le bon ordre.</p>`
            : `<p>Pour construire le triangle ${L1}${L2}${L3} connaissant un côté et les deux angles adjacents (${a} cm, ${alpha}°, ${beta}°), on suit 5 étapes. Remets-les dans l'ordre.</p>`;
    }

    const payload = makeOrdonner(items, texte, opts(L_ETAP));
    payload._meta.generatorKey = 'etapes_construction_assoc';
    return payload;
};

// ─── Ex. 5 — Codage du dessin à main levée (QCM) ────────────
const choixSousCas1 = (L1, L2, L3, alpha) => [
    // Correct
    `Un trait sur [${L1}${L2}] et un trait sur [${L1}${L3}] (côtés égaux), un arc au sommet ${L1} annoté ${alpha}°.`,
    // D1
    `Un trait sur [${L1}${L2}] et un trait sur [${L2}${L3}], un arc au sommet ${L1} annoté ${alpha}°.`,
    // D2
    `Un trait sur [${L1}${L2}] et un trait sur [${L1}${L3}], un petit carré au sommet ${L1}.`,
    // D3
    `Un trait sur [${L1}${L2}] et un trait sur [${L1}${L3}], un arc au sommet ${L2} annoté ${alpha}°.`,
];

const choixSousCas2 = (L1, L2, L3, a, b) => [
    // Correct
    `Longueur ${a} cm notée sur [${L1}${L2}], longueur ${b} cm notée sur [${L2}${L3}], un petit carré au sommet ${L2}.`,
    // D1
    `Longueur ${a} cm notée sur [${L1}${L2}], longueur ${b} cm notée sur [${L2}${L3}], un petit carré au sommet ${L1}.`,
    // D2
    `Longueur ${a} cm notée sur [${L1}${L2}], longueur ${b} cm notée sur [${L2}${L3}], un arc au sommet ${L2} annoté 90°.`,
    // D3
    `Longueur ${a} cm notée sur [${L1}${L2}], longueur ${b} cm notée sur [${L2}${L3}], un trait sur [${L1}${L2}] et un trait sur [${L2}${L3}], un petit carré au sommet ${L2}.`,
];

// PAS DE BARRE VERTICALE ici : `isComplexAnswer` (utils/questionUtils.js)
// déclare « format [phrase|valeur] » tout texte contenant un crochet ET une
// barre, même séparés. Les côtés étant nommés [AB], un « | » dessinant le
// codage faisait basculer le scoring sur compareComplexAnswers : la bonne
// réponse était comptée fausse à 100 % de ce sous-cas.
const choixSousCas3 = (L1, L2, L3, a) => [
    // Correct
    `Longueur ${a} cm notée sur un côté, et un même petit trait sur les trois côtés [${L1}${L2}], [${L2}${L3}] et [${L1}${L3}].`,
    // D1
    `Longueur ${a} cm notée sur un côté, et un même petit trait sur les deux côtés [${L1}${L2}] et [${L2}${L3}] seulement.`,
    // D2
    `Longueur ${a} cm notée sur un côté, un petit carré au sommet ${L1}.`,
    // D3
    // 90° et non 60° : les trois angles d'un triangle équilatéral VALENT 60°,
    // si bien que ce distracteur énonçait des faits vrais et constituait, avec
    // la longueur, un codage exact — compté faux. 90° aux trois sommets est
    // impossible (somme 270°) et vise la même confusion : coder les angles
    // plutôt que les côtés.
    `Longueur ${a} cm notée sur un côté, un arc de 90° à chacun des trois sommets ${L1}, ${L2} et ${L3}.`,
];

const genCodage = () => {
    const r = Math.random();
    const sousCas = r < 0.40 ? 1 : (r < 0.75 ? 2 : 3);
    const [L1, L2, L3] = pickLetters(3);
    const variante = randomChoice(['C1', 'C2']);

    let choix, bonne, donnees;
    if (sousCas === 1) {
        const a = randomChoice(LONGUEURS_EX3);
        // Ni 90° (déjà hors du pool), ni 60° : avec 60° au sommet, l'isocèle
        // est en réalité ÉQUILATÉRAL. Ses trois côtés sont alors égaux et ses
        // trois angles valent 60°, si bien que deux des distracteurs énonçaient
        // des faits vrais et étaient des codages exacts comptés faux.
        const alpha = randomChoice(ANGLES_EX3.filter(v => v !== 60));
        donnees = `${L1}${L2} = ${a} cm, ${L1}${L3} = ${a} cm et l'angle en ${L1} mesure ${alpha}°`;
        choix = choixSousCas1(L1, L2, L3, alpha);
        bonne = choix[0];
    } else if (sousCas === 2) {
        const [a, b] = randomChoiceMultiple(LONGUEURS_EX3, 2); // garantit a ≠ b
        donnees = `${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et l'angle en ${L2} est droit`;
        choix = choixSousCas2(L1, L2, L3, a, b);
        bonne = choix[0];
    } else {
        const a = randomChoice(LONGUEURS_EX3);
        donnees = `${L1}${L2}${L3} est équilatéral de côté ${a} cm`;
        choix = choixSousCas3(L1, L2, L3, a);
        bonne = choix[0];
    }

    // « les bonnes notations » et non « correct » : le distracteur du sous-cas 2
    // (arc annoté 90° au lieu du petit carré) porte une information EXACTE. Ce
    // qu'on évalue est le respect de la convention de codage, pas la vérité de
    // l'énoncé — autant le demander.
    const texte = variante === 'C1'
        ? `<p>Tu dois construire le triangle ${L1}${L2}${L3} tel que ${donnees}. Pour ton dessin à main levée, quel codage utilise les bonnes notations ?</p>`
        : `<p>Pour préparer la construction du triangle ${L1}${L2}${L3} (${donnees}), on fait un dessin à main levée. Quel codage emploie les notations correctes ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_COD));
    payload._meta.generatorKey = 'codage_main_levee_qcm';
    return payload;
};

// ─── Ex. 6 — Cohérence des données (V/F) ────────────────────

// Tire 3 lettres distinctes à chaque affirmation pour maximiser la variété.
const buildAffirmations = () => {
    // V1 — 3 côtés donnés (constructible)
    const makeV1 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const [a, b, c] = shuffle(randomChoice(TRIPLETS_OUI));
        return {
            id: 'V1',
            verite: 0,
            texte: `On connaît ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // V2 — 2 côtés + angle compris
    const makeV2 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const [a, c] = randomChoiceMultiple(LONGUEURS_EX3, 2);
        const alpha = randomChoice(ANGLES_EX3);
        return {
            id: 'V2',
            verite: 0,
            texte: `On connaît ${L1}${L2} = ${a} cm, ${L1}${L3} = ${c} cm et l'angle en ${L1} mesure ${alpha}° : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // V3 — 1 côté + 2 angles adjacents (alpha + beta < 170)
    const makeV3 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const b = randomChoice(LONGUEURS_EX3);
        let alpha, beta, tries = 0;
        do {
            alpha = randomChoice(ANGLES_EX3);
            beta = randomChoice(ANGLES_EX3);
            tries++;
        } while ((alpha + beta >= 170 || alpha === beta) && tries < 30);
        if (alpha + beta >= 170 || alpha === beta) { alpha = 40; beta = 60; }
        return {
            id: 'V3',
            verite: 0,
            texte: `On connaît ${L2}${L3} = ${b} cm, l'angle en ${L2} mesure ${alpha}° et l'angle en ${L3} mesure ${beta}° : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // V4 — équilatéral
    const makeV4 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const a = randomChoice(LONGUEURS_EX3);
        return {
            id: 'V4',
            verite: 0,
            texte: `On sait que le triangle ${L1}${L2}${L3} est équilatéral de côté ${a} cm : on peut le construire.`,
        };
    };
    // V5 — isocèle avec les 2 côtés donnés
    const makeV5 = () => {
        const [L1, L2, L3] = pickLetters(3);
        // Isocèle en L1 : L1L2 = L1L3 = a, et la base L2L3 = c. Les trois côtés
        // sont donc a, a et c, et le triangle N'EXISTE QUE SI c < 2a. Tirer c
        // librement dans le pool produisait des bases trop longues étiquetées
        // « Vrai » : l'élève qui appliquait l'inégalité triangulaire répondait
        // Faux et était compté faux.
        const a = randomChoice(LONGUEURS_EX3);
        const c = randomChoice(LONGUEURS_EX3.filter(v => v !== a && v < 2 * a));
        return {
            id: 'V5',
            verite: 0,
            texte: `On sait que le triangle ${L1}${L2}${L3} est isocèle en ${L1} avec ${L1}${L2} = ${a} cm et ${L2}${L3} = ${c} cm : on peut le construire.`,
        };
    };
    // F1 — 2 côtés seulement (infinité de triangles)
    const makeF1 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const [a, b] = randomChoiceMultiple(LONGUEURS_EX3, 2);
        return {
            id: 'F1',
            verite: 1,
            texte: `On connaît ${L1}${L2} = ${a} cm et ${L2}${L3} = ${b} cm : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // F2 — 3 angles (triangles semblables, pas unique)
    const makeF2 = () => {
        const [L1, L2, L3] = pickLetters(3);
        let alpha, beta, gamma, tries = 0;
        do {
            alpha = randomChoice(ANGLES_EX3);
            beta = randomChoice(ANGLES_EX3);
            gamma = 180 - alpha - beta;
            tries++;
        } while ((gamma < 20 || gamma > 150 || alpha === beta || beta === gamma || alpha === gamma) && tries < 40);
        if (gamma < 20 || gamma > 150 || alpha === beta || beta === gamma || alpha === gamma) {
            // Fallback sûr : triangle scalène
            alpha = 50; beta = 70; gamma = 60;
        }
        return {
            id: 'F2',
            verite: 1,
            texte: `On connaît les trois angles : en ${L1} c'est ${alpha}°, en ${L2} c'est ${beta}° et en ${L3} c'est ${gamma}° (somme 180°). On peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // F3 — 3 côtés dont un triplet non constructible
    const makeF3 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const [a, b, c] = shuffle(randomChoice(TRIPLETS_NON));
        return {
            id: 'F3',
            verite: 1,
            texte: `On connaît ${L1}${L2} = ${a} cm, ${L2}${L3} = ${b} cm et ${L1}${L3} = ${c} cm : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // F4 — 1 côté + 1 angle (pas assez)
    const makeF4 = () => {
        const [L1, L2, L3] = pickLetters(3);
        const a = randomChoice(LONGUEURS_EX3);
        const alpha = randomChoice(ANGLES_EX3);
        return {
            id: 'F4',
            verite: 1,
            texte: `On connaît ${L1}${L2} = ${a} cm et l'angle en ${L1} mesure ${alpha}° : on peut construire un unique triangle ${L1}${L2}${L3}.`,
        };
    };
    // F5 — isocèle sans longueurs (infinité de triangles similaires)
    const makeF5 = () => {
        const [L1, L2, L3] = pickLetters(3);
        return {
            id: 'F5',
            verite: 1,
            texte: `On sait que le triangle ${L1}${L2}${L3} est isocèle en ${L1} : on peut le construire de manière unique.`,
        };
    };

    return {
        VRAIS: { V1: makeV1, V2: makeV2, V3: makeV3, V4: makeV4, V5: makeV5 },
        FAUX:  { F1: makeF1, F2: makeF2, F3: makeF3, F4: makeF4, F5: makeF5 },
    };
};

const genCoherence = () => {
    // Retry loop : si deux affirmations produisent un texte identique (très rare),
    // makeVraiFaux dédupliquerait → on préfère régénérer pour rester à 6 lignes.
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmations();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            const texte = `<p>Pour chaque jeu de données proposé, indique si l'on peut construire un unique triangle.</p>`;
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_COH));
            payload._meta.generatorKey = 'coherence_donnees_vf';
            return payload;
        }
        tries++;
    }

    // Fallback : si on n'arrive pas à 6 lignes distinctes, on accepte ce qui est là.
    const builders = buildAffirmations();
    const vrais = ['V1', 'V2', 'V3'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F2', 'F3'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = `<p>Pour chaque jeu de données proposé, indique si l'on peut construire un unique triangle.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_COH));
    payload._meta.generatorKey = 'coherence_donnees_vf';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
// ═══════════════════════════════════════════════════════════
// Construction interactive d'un triangle (programme_construction)
// ═══════════════════════════════════════════════════════════

const PX_PER_CM = 30;
// 720 x 520 et non 600 x 400 : un rayon peut atteindre 8 cm, soit 240 px, et
// TOUS les cercles débordaient du cadre (100 % des tirages SSS et SAS,
// jusqu'à 180 px sous la figure). On agrandit le cadre plutôt que de réduire
// PX_PER_CM : le composant affiche « 1 carreau = 1 cm » avec une grille
// câblée à 30 px, changer l'échelle rendrait cette phrase fausse.
// Contraintes : centre à y >= 240 et h >= centre + 240 ; base centrée donc
// w >= 480 + 30 x base, soit 720 pour une base de 8 cm.
const VIEWBOX_CONSTR = { w: 720, h: 540 };
const TOL_CONSTR = 12;
const COLOR_BLEU_CONSTR = '#2563EB';
const COLOR_VERT_CONSTR = '#1D9E75';

// Triplets constructibles avec toutes les longueurs ≤ 8 cm (pour tenir dans le viewBox).
const TRIPLETS_CONSTR = TRIPLETS_OUI.filter(([a, b, c]) => a >= 3 && c <= 8);
// Longueurs utilisables pour SAS/ASA (un côté pré-tracé).
const LONGUEURS_CONSTR = [3, 4, 5, 6, 7, 8];
// Angles utilisables pour SAS (angle compris entre 2 côtés).
const ANGLES_SAS = [30, 40, 45, 50, 60, 70, 75, 80, 90, 100, 110, 120];
// Angles utilisables pour ASA (2 angles adjacents, β + γ ≤ 140).
const ANGLES_ASA = [30, 40, 45, 50, 60, 70];

const toRad = (d) => (d * Math.PI) / 180;

// Placement d'un point : kind 'circle' avec r=0 (pas de droite tracée).
// Idiome documenté dans gen_654.js:350 — le renderer affiche juste un disque au centre.
const pointPlaceShape = (p) => ({ type: 'circle', cx: p.x, cy: p.y, r: 0 });

// Méthode 1 — SSS : 3 longueurs (règle + compas)
const genConstructionSSS = () => {
    const [lenA, lenB, lenC] = shuffle(randomChoice(TRIPLETS_CONSTR));
    // Convention : a = BC (base), b = CA, c = AB. Base tracée horizontalement.
    const [B_name, C_name, A_name] = pickLetters(3);
    const a = lenA; // BC
    const b = lenB; // CA
    const c = lenC; // AB

    // Centrage de la base dans le viewBox.
    const baseWidthPx = a * PX_PER_CM;
    const Bx = Math.round((VIEWBOX_CONSTR.w - baseWidthPx) / 2);
    const By = 280;   // 280 - 240 = 40 en haut, 280 + 240 = 520 en bas
    const Cx = Bx + baseWidthPx;
    const Cy = By;

    // Intersection des cercles (B, c) et (C, b), A au-dessus.
    const aProj = (c * c - b * b + a * a) / (2 * a); // cm depuis B le long de [BC]
    const hSq = c * c - aProj * aProj;
    if (hSq < 0) return genConstructionSSS(); // garde-fou, ne devrait pas arriver
    const h = Math.sqrt(hSq);
    const A_target = { x: Bx + aProj * PX_PER_CM, y: By - h * PX_PER_CM };

    const texte =
        `<p>Construis le triangle ${A_name}${B_name}${C_name} tel que `
        + `${B_name}${C_name} = <b>${a} cm</b>, ${C_name}${A_name} = <b>${b} cm</b> et `
        + `${A_name}${B_name} = <b>${c} cm</b>. La base [${B_name}${C_name}] est déjà tracée.</p>`;
        // Pas de rappel de méthode dans l'énoncé : il donnait la marche à suivre
        // en clair à qui n'en avait pas besoin. Il est passé au coup de pouce.

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOL_CONSTR,
            viewBox: VIEWBOX_CONSTR,
            points: [
                { name: B_name, x: Bx, y: By },
                { name: C_name, x: Cx, y: Cy },
            ],
            preShapes: [{ type: 'line', from: B_name, to: C_name }],
            tasks: [
                {
                    id: 'arc_B',
                    consigne: `Trace en <b>bleu</b> l'arc de centre ${B_name} et de rayon ${c} cm (= longueur ${A_name}${B_name}).`,
                    color: COLOR_BLEU_CONSTR,
                    shape: { type: 'circle', cx: Bx, cy: By, r: c * PX_PER_CM },
                },
                {
                    id: 'arc_C',
                    consigne: `Trace en <b>bleu</b> l'arc de centre ${C_name} et de rayon ${b} cm (= longueur ${C_name}${A_name}).`,
                    color: COLOR_BLEU_CONSTR,
                    shape: { type: 'circle', cx: Cx, cy: Cy, r: b * PX_PER_CM },
                },
                {
                    id: 'point_A',
                    // « au-dessus de [BC] » : les deux arcs se coupent en DEUX
                    // points, symétriques par rapport à la base, et les deux donnent
                    // un triangle aux mêmes longueurs. Sans cette précision, l'élève
                    // qui choisit le point du bas construit juste et est compté faux
                    // (100 % des tirages). Le second point est visible depuis que le
                    // viewBox contient les cercles entiers.
                    consigne: `Place en <b>vert</b> le sommet ${A_name} à l'intersection des deux arcs, <b>au-dessus</b> de [${B_name}${C_name}].`,
                    color: COLOR_VERT_CONSTR,
                    shape: pointPlaceShape(A_target),
                    pointLabel: A_name,
                    // Les deux arcs se croisent en DEUX points : le halo vert dit
                    // lequel viser, ce que la consigne dit déjà par écrit.
                    showTarget: true,
                    // Auto-tracé des 2 segments finaux à la validation du point.
                    extraShapes: [
                        { type: 'segment', p1: { x: Bx, y: By }, p2: A_target, color: COLOR_VERT_CONSTR },
                        { type: 'segment', p1: { x: Cx, y: Cy }, p2: A_target, color: COLOR_VERT_CONSTR },
                    ],
                },
            ],
        },
        texte,
        opts(L_CONSTR_SSS),
    );
    payload._meta.generatorKey = 'construction_interactive_triangle_sss';
    return payload;
};

// Méthode 2 — SAS : 2 longueurs + angle compris (règle + rapporteur + compas)
const genConstructionSAS = () => {
    const [A_name, B_name, C_name] = pickLetters(3);
    const c = randomChoice(LONGUEURS_CONSTR); // AB
    const b = randomChoice(LONGUEURS_CONSTR); // AC
    const alpha = randomChoice(ANGLES_SAS);   // angle BAC

    const baseWidthPx = c * PX_PER_CM;
    const Ax = Math.round((VIEWBOX_CONSTR.w - baseWidthPx) / 2);
    // 280 et non 260 : à 260, un côté de 8 cm avec un angle proche de 90°
    // posait le sommet à y = 20, assez pour le point mais pas pour son
    // étiquette (1,2 % des tirages). À 280 il reste 40 px de marge.
    const Ay = 280;
    const Bx = Ax + baseWidthPx;
    const By = Ay;

    const C_target = {
        x: Ax + b * PX_PER_CM * Math.cos(toRad(alpha)),
        y: Ay - b * PX_PER_CM * Math.sin(toRad(alpha)),
    };

    const texte =
        `<p>Construis le triangle ${A_name}${B_name}${C_name} tel que `
        + `${A_name}${B_name} = <b>${c} cm</b>, ${A_name}${C_name} = <b>${b} cm</b> et `
        + `l'angle en ${A_name} mesure <b>${alpha}°</b>. Le côté [${A_name}${B_name}] est déjà tracé.</p>`;
        // Pas de rappel de méthode dans l'énoncé : il donnait la marche à suivre
        // en clair à qui n'en avait pas besoin. Il est passé au coup de pouce.

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOL_CONSTR,
            viewBox: VIEWBOX_CONSTR,
            points: [
                { name: A_name, x: Ax, y: Ay },
                { name: B_name, x: Bx, y: By },
            ],
            preShapes: [{ type: 'line', from: A_name, to: B_name }],
            tasks: [
                {
                    id: 'ray_alpha',
                    kind: 'angle_ray',
                    consigne: `Place le rapporteur sur ${A_name}, aligne la base sur [${A_name}${B_name}), puis clique à <b>${alpha}°</b> pour tracer une demi-droite.`,
                    color: COLOR_BLEU_CONSTR,
                    tolerance_deg: 2,
                    shape: { type: 'ray', from: A_name, angleDeg: alpha },
                },
                {
                    id: 'arc_b',
                    consigne: `Trace en <b>bleu</b> l'arc de centre ${A_name} et de rayon ${b} cm.`,
                    color: COLOR_BLEU_CONSTR,
                    shape: { type: 'circle', cx: Ax, cy: Ay, r: b * PX_PER_CM },
                },
                {
                    id: 'point_C',
                    consigne: `Place en <b>vert</b> le sommet ${C_name} à l'intersection de l'arc et de la demi-droite.`,
                    color: COLOR_VERT_CONSTR,
                    shape: pointPlaceShape(C_target),
                    pointLabel: C_name,
                    // Le halo vert marque l'intersection à cliquer.
                    showTarget: true,
                    // Le côté [AC] n'est porté par aucune tâche : la demi-droite du
                    // rapporteur dépasse le sommet et n'est pas le côté. Sans ce
                    // tracé automatique, ni l'élève ni le corrigé prof ne voyaient le
                    // triangle refermé — seulement deux côtés sur trois.
                    extraShapes: [
                        { type: 'segment', p1: { x: Ax, y: Ay }, p2: C_target, color: COLOR_VERT_CONSTR },
                    ],
                },
                {
                    id: 'seg_BC',
                    kind: 'segment',
                    consigne: `Trace en <b>vert</b> le segment [${B_name}${C_name}] pour fermer le triangle.`,
                    color: COLOR_VERT_CONSTR,
                    shape: { type: 'segment', p1: { x: Bx, y: By }, p2: C_target },
                },
            ],
        },
        texte,
        opts(L_CONSTR_SAS),
    );
    payload._meta.generatorKey = 'construction_interactive_triangle_sas';
    return payload;
};

// Méthode 3 — ASA : 1 longueur + 2 angles adjacents (règle + rapporteur)
const genConstructionASA = () => {
    const [B_name, C_name, A_name] = pickLetters(3);
    const a = randomChoice(LONGUEURS_CONSTR); // BC
    let beta, gamma;
    do {
        beta = randomChoice(ANGLES_ASA);
        gamma = randomChoice(ANGLES_ASA);
    } while (beta + gamma > 140); // laisser ≥ 40° pour le 3e angle (lisibilité)

    const baseWidthPx = a * PX_PER_CM;
    const Bx = Math.round((VIEWBOX_CONSTR.w - baseWidthPx) / 2);
    // Pas de cercle ici, mais le sommet peut monter à 351 px au-dessus de la
    // base : une base basse lui laisse 70 px de marge en haut au lieu de 10.
    const By = 420;
    const Cx = Bx + baseWidthPx;
    const Cy = By;

    // Intersection des demi-droites [B, angle=β) et [C, angle=180−γ). Loi des sinus.
    const sumBG = beta + gamma;
    const lenBA = (a * Math.sin(toRad(gamma))) / Math.sin(toRad(sumBG)); // en cm
    const A_target = {
        x: Bx + lenBA * PX_PER_CM * Math.cos(toRad(beta)),
        y: By - lenBA * PX_PER_CM * Math.sin(toRad(beta)),
    };

    const texte =
        `<p>Construis le triangle ${A_name}${B_name}${C_name} tel que `
        + `${B_name}${C_name} = <b>${a} cm</b>, l'angle en ${B_name} mesure <b>${beta}°</b> et `
        + `l'angle en ${C_name} mesure <b>${gamma}°</b>. Le côté [${B_name}${C_name}] est déjà tracé.</p>`;
        // Pas de rappel de méthode dans l'énoncé : il donnait la marche à suivre
        // en clair à qui n'en avait pas besoin. Il est passé au coup de pouce.

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOL_CONSTR,
            viewBox: VIEWBOX_CONSTR,
            points: [
                { name: B_name, x: Bx, y: By },
                { name: C_name, x: Cx, y: Cy },
            ],
            preShapes: [{ type: 'line', from: B_name, to: C_name }],
            tasks: [
                {
                    id: 'ray_beta',
                    kind: 'angle_ray',
                    consigne: `Place le rapporteur sur ${B_name}, aligne la base sur [${B_name}${C_name}), puis clique à <b>${beta}°</b>.`,
                    color: COLOR_BLEU_CONSTR,
                    tolerance_deg: 2,
                    shape: { type: 'ray', from: B_name, angleDeg: beta },
                },
                {
                    id: 'ray_gamma',
                    kind: 'angle_ray',
                    consigne: `Place le rapporteur sur ${C_name}, aligne la base sur [${C_name}${B_name}), puis clique à <b>${gamma}°</b> (du même côté que la 1re demi-droite).`,
                    color: COLOR_BLEU_CONSTR,
                    tolerance_deg: 2,
                    shape: { type: 'ray', from: C_name, angleDeg: 180 - gamma },
                },
                {
                    id: 'point_A',
                    consigne: `Place en <b>vert</b> le sommet ${A_name} à l'intersection des deux demi-droites.`,
                    color: COLOR_VERT_CONSTR,
                    shape: pointPlaceShape(A_target),
                    pointLabel: A_name,
                    // Le halo vert marque l'intersection à cliquer.
                    showTarget: true,
                },
                {
                    id: 'seg_BA',
                    kind: 'segment',
                    consigne: `Trace en <b>vert</b> le segment [${B_name}${A_name}].`,
                    color: COLOR_VERT_CONSTR,
                    shape: { type: 'segment', p1: { x: Bx, y: By }, p2: A_target },
                },
                {
                    id: 'seg_CA',
                    kind: 'segment',
                    consigne: `Trace en <b>vert</b> le segment [${C_name}${A_name}].`,
                    color: COLOR_VERT_CONSTR,
                    shape: { type: 'segment', p1: { x: Cx, y: Cy }, p2: A_target },
                },
            ],
        },
        texte,
        opts(L_CONSTR_ASA),
    );
    payload._meta.generatorKey = 'construction_interactive_triangle_asa';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Registry & Export
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    faisabilite_triangle_qcm:   genFaisabilite,
    longueur_extreme_saisie:    genLongueurExtreme,
    methode_construction_qcm:   genMethode,
    etapes_construction_assoc:  genEtapes,
    codage_main_levee_qcm:      genCodage,
    coherence_donnees_vf:       genCoherence,
    construction_interactive_triangle_sss: genConstructionSSS,
    construction_interactive_triangle_sas: genConstructionSAS,
    construction_interactive_triangle_asa: genConstructionASA,
};

export const CATEGORIES = [
    {
        id: 'inegalite_triangulaire',
        label: 'Inégalité triangulaire',
        formatsList: [
            { key: 'faisabilite_triangle_qcm', label: 'Faisabilité (QCM)' },
            { key: 'longueur_extreme_saisie',  label: 'Longueur extrême (saisie)' },
        ],
    },
    {
        id: 'suffisance_donnees',
        label: 'Suffisance des données',
        formatsList: [
            { key: 'coherence_donnees_vf', label: 'Cohérence (V/F)' },
        ],
    },
    {
        id: 'methode_construction_triangle',
        label: 'Choisir la méthode',
        formatsList: [
            { key: 'methode_construction_qcm', label: 'Méthode (QCM)' },
        ],
    },
    {
        id: 'procedure_construction_triangle',
        label: 'Procédure et codage',
        formatsList: [
            { key: 'etapes_construction_assoc', label: 'Étapes (ordonner)' },
            { key: 'codage_main_levee_qcm',     label: 'Codage (QCM)' },
        ],
    },
    {
        id: 'construction_interactive_triangle',
        label: 'Construire un triangle (interactif)',
        formatsList: [
            { key: 'construction_interactive_triangle_sss', label: 'SSS (3 côtés)' },
            { key: 'construction_interactive_triangle_sas', label: 'SAS (2 côtés + angle)' },
            { key: 'construction_interactive_triangle_asa', label: 'ASA (1 côté + 2 angles)' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};
